import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink } from "lucide-react";
import Layout from "../components/Layout";
import api, { errMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Billing() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [billing, setBilling] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const r = await api.get("/billing/me");
        if (!cancelled) setBilling(r.data);
      } catch (err) {
        console.warn("billing/me load failed:", err?.message || err);
      }
    };

    const poll = async () => {
      if (!sessionId) {
        setPolling(false);
        return;
      }
      for (let i = 0; i < 6; i++) {
        try {
          const r = await api.get(`/billing/checkout/${sessionId}`);
          if (
            r.data.subscription_status === "trialing" ||
            r.data.subscription_status === "active" ||
            r.data.payment_status === "paid"
          ) {
            await refreshUser();
            await loadStatus();
            toast.success("Subscription active!");
            setPolling(false);
            return;
          }
        } catch (err) {
          console.warn(`checkout poll attempt ${i + 1} failed:`, err?.message || err);
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
      setPolling(false);
    };

    loadStatus();
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll/loadStatus are inlined
  }, [sessionId, refreshUser]);

  const openPortal = async () => {
    try {
      const r = await api.post("/billing/portal", {
        return_url: window.location.origin + "/billing",
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(errMessage(e, "Portal unavailable"));
    }
  };

  return (
    <Layout>
      <div className="px-8 py-10 max-w-3xl">
        <div className="overline mb-3">// Billing</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mb-6">
          Subscription.
        </h1>

        {polling && (
          <div className="surface-card p-6 mb-6 flex items-center gap-3">
            <div className="w-2 h-2 bg-[--accent] animate-pulse" />
            <div className="text-sm">Confirming your subscription with Stripe…</div>
          </div>
        )}

        {sessionId && !polling && billing?.has_active_access && (
          <div className="surface-card p-6 mb-6 flex items-start gap-3 border-l-4 border-l-[--success]">
            <CheckCircle2 size={20} className="text-[--success] mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-display font-bold mb-1">You're in.</div>
              <div className="text-sm text-[--ink-soft]">
                Free trial active for {billing.trial_ends_at ? new Date(billing.trial_ends_at).toLocaleDateString() : "14 days"}. We'll only charge after the trial ends.
              </div>
            </div>
          </div>
        )}

        <div data-testid="billing-summary" className="surface-card p-6 mb-6">
          <div className="overline mb-4">Current plan</div>
          {billing ? (
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-[--ink-muted] mb-1">Tier</div>
                <div className="font-display font-bold text-2xl uppercase">
                  {billing.subscription_tier || "—"}
                </div>
                {billing.is_grandfathered && (
                  <div className="text-xs font-mono text-[--success] mt-1">comped (grandfathered)</div>
                )}
              </div>
              <div>
                <div className="text-xs text-[--ink-muted] mb-1">Status</div>
                <div className="font-mono">{billing.subscription_status || (billing.is_grandfathered ? "active" : "none")}</div>
                {billing.trial_ends_at && (
                  <div className="text-xs text-[--ink-muted] mt-2">
                    Trial ends {new Date(billing.trial_ends_at).toLocaleDateString()}
                  </div>
                )}
                {billing.current_period_end && (
                  <div className="text-xs text-[--ink-muted] mt-1">
                    Renews {new Date(billing.current_period_end).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[--ink-muted]">Loading…</div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {billing?.has_stripe_customer && (
            <button
              data-testid="manage-subscription-btn"
              onClick={openPortal}
              className="btn-primary flex items-center gap-2"
            >
              <ExternalLink size={14} strokeWidth={1.5} /> Manage subscription
            </button>
          )}
          {!billing?.has_active_access && (
            <Link
              to="/pricing"
              data-testid="goto-pricing-btn"
              className="btn-primary"
            >
              See plans →
            </Link>
          )}
        </div>
      </div>
    </Layout>
  );
}
